import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NzMessageService } from 'ng-zorro-antd/message';
import { AnyoComponent } from 'projects/app-core/src/app/models/AnyoComponent';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import {
  ALL_CONTRACT_BENEFITS,
  BenefitVisibilityResponse,
  CompanyVisibilityUpdateRequest,
  CompanyVisibilityVerticalRow,
  ContractBenefit,
  ContractSettings,
  PolicyRule,
  PolicyRulePayload,
  PolicyService,
  TherapistFilterRulePayload,
} from './policy.service';
import { MasterdataService } from '../../../services/masterdata.service';
import { TherapyVerticals } from 'projects/app-core/src/models/TherapyVerticals';
import { TherapyConcerns } from 'projects/app-core/src/models/TherapyConcerns';
import { TherapistExpertLevel } from 'projects/app-core/src/models/TherapistExpertLevel';
import { TherapistService } from '../../../services/therapist.service';
import { IAnyoTherapist } from '../../../models/IAnyoTherapist';

// ── UI-only interfaces ─────────────────────────────────────────────────────────

export interface ConditionRow {
  fact: string;
  operator: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any; // string | number | boolean — kept as any for template two-way binding compatibility
  params?: Record<string, unknown>; // for parameterized facts (e.g. sessionsInLastNDays { days: 7 })
}

export interface PolicyRuleUI {
  id?: string;
  name: string;
  description?: string;
  priority: number;
  active: boolean;
  action: string;
  companyId: string;
  isGlobal: boolean; // true = Global owner, read-only on this page
  /** Set only for a personal (per-user) rule — this page only ever displays these read-only, never creates/edits them. */
  userId?: string;
  scope: { verticalId?: string[]; concernId?: string[]; expertLevelId?: string; therapistIds?: string[]; benefitCategory?: ContractBenefit };
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: ConditionRow[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  outputs: Record<string, any>; // kept as any for template two-way binding compatibility
  expanded: boolean;
  saving: boolean;
}

export interface TfRuleUI {
  id?: string;
  name: string;
  priority: number;
  active: boolean;
  expanded: boolean;
  maxRate: number | null;
  internalOnly: boolean;
  excludeTags: string[];
  tagInput: string;
  saving: boolean;
}

// ── Facts, outputs and operators (mirrors policy-vue.html constants) ────────────

const FACTS: Record<string, { value: string; label: string; type: 'number' | 'bool'; description?: string; hasParams?: boolean; paramsLabel?: string }[]> = {
  'Session Booking': [
    { value: 'sessionsInLastNDays', label: 'Sessions booked in last N days (this vertical + concern)', type: 'number', hasParams: true, paramsLabel: 'Days' },
    { value: 'totalSessionsInLastNDays', label: 'Sessions booked in last N days (all verticals)', type: 'number', hasParams: true, paramsLabel: 'Days' },
    { value: 'sessionsConsumedInPeriod', label: 'Sessions booked (this concern)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForVertical', label: 'Sessions booked (this vertical)', type: 'number' },
    { value: 'totalSessionsConsumedInPeriod', label: 'Sessions booked (all verticals)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeVerticals', label: 'Sessions booked (selected verticals combined)', type: 'number' },
    { value: 'maxSessionsConsumedInPeriodAcrossScopeVerticals', label: 'Sessions booked (max across any selected vertical)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeConcerns', label: 'Sessions booked (selected concerns combined)', type: 'number' },
    { value: 'maxSessionsConsumedInPeriodAcrossScopeConcerns', label: 'Sessions booked (max across any selected concern)', type: 'number' },
    { value: 'sessionsConsumedInPeriodForScopeVerticalAndConcern', label: 'Sessions booked (selected verticals + concerns combined)', type: 'number' },
    { value: 'lastSessionDateForScopeVerticals', label: 'Last session date (selected verticals)', type: 'number' },
    { value: 'activeHealthProgramEnrollmentsCount', label: 'Active health program enrollments', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
    { value: 'hasHadSessionWithTherapist', label: 'Has had session with this therapist', type: 'bool' },
    { value: 'sessionsWithThisTherapist', label: 'Session count with this expert', type: 'number' },
    { value: 'currentVerticalIsHealthProgram', label: 'Is a health program vertical', type: 'bool' },
  ],
  'Event Registration': [
    { value: 'eventsConsumedInPeriod', label: 'Events registered this period', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
  'Diagnostic Order': [
    { value: 'diagnosticsOrdersConsumedInPeriod', label: 'Diagnostic orders this period', type: 'number' },
    { value: 'diagnosticsSpendInPeriod', label: 'Diagnostic spend (INR)', type: 'number' },
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
  ],
  'Benefit Visibility': [
    { value: 'isFamilyMember', label: 'Is family member / dependent', type: 'bool' },
    { value: 'corporateUser', label: 'Is corporate employee', type: 'bool' },
  ],
};

const OUTPUTS: Record<
  string,
  { key: string; label: string; type: 'select' | 'number' | 'text'; options?: { v: string; l: string }[] }[]
> = {
  'Session Booking': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'sessionMinutes', label: 'Session duration', type: 'select',
      options: [{v:'20',l:"20 min"}, { v: '30', l: '30 min' }, { v: '45', l: '45 min' }, { v: '60', l: '60 min' }, { v: '90', l: '90 min' }],
    },
    {
      key: 'sessionAllowed', label: 'Allow booking', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
    { key: 'cooldown', label: 'Min. days between sessions', type: 'number' },
    { key: 'blockReason', label: 'Block reason (shown to user)', type: 'text' },
    // Self-pay pricing — only shown when payer='user'. GST and markup RATE come from
    // Contract Settings (partner-level); this rule only picks the pricing MODE and,
    // for slab mode, the markup VALUE on top of it.
    {
      key: 'selfPayPricingMode', label: 'Self-pay pricing', type: 'select',
      options: [
        { v: 'slab', l: 'Therapist slab pricing + markup' },
        { v: 'flat', l: 'Flat amount' },
        { v: 'flatSlab', l: 'Flat amount by session duration' },
      ],
    },
    {
      key: 'selfPaySlabSource', label: 'Slab pricing source', type: 'select',
      options: [{ v: 'corporate', l: 'Corporate slab' }, { v: 'retail', l: 'Retail slab' }],
    },
    {
      key: 'selfPayMarkupType', label: 'Markup type', type: 'select',
      options: [{ v: 'percent', l: 'Percent (%)' }, { v: 'fixed', l: 'Fixed amount (₹)' }],
    },
    { key: 'selfPayMarkupValue', label: 'Markup value', type: 'number' },
    { key: 'selfPayFlatAmount', label: 'Flat amount (₹)', type: 'number' },
  ],
  'Event Registration': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'eventAllowed', label: 'Allow registration', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
  ],
  'Diagnostic Order': [
    {
      key: 'payer', label: 'Who pays', type: 'select',
      options: [{ v: 'company', l: 'Company' }, { v: 'user', l: 'Employee / user' }],
    },
    {
      key: 'diagAllowed', label: 'Allow ordering', type: 'select',
      options: [{ v: 'true', l: 'Yes — allow' }, { v: 'false', l: 'No — block' }],
    },
  ],
  'Benefit Visibility': [
    {
      key: 'visible', label: 'Benefit visible', type: 'select',
      options: [{ v: 'true', l: 'Yes — show benefit' }, { v: 'false', l: 'No — hide benefit' }],
    },
  ],
};

// Maps each action to the output key that controls whether the action is allowed.
// When that key is 'false', payer + other booking outputs are irrelevant.
const ACTION_ALLOWED_FIELD: Record<string, string> = {
  'Session Booking': 'sessionAllowed',
  'Event Registration': 'eventAllowed',
  'Diagnostic Order': 'diagAllowed',
};

// Output keys that must be cleared/disabled when the action is blocked.
// blockReason is intentionally excluded — it is still meaningful when blocked.
const BLOCKED_CLEARS: Record<string, string[]> = {
  'Session Booking': ['payer', 'sessionMinutes', 'cooldown'],
  'Event Registration': ['payer'],
  'Diagnostic Order': ['payer'],
};

// Self-pay pricing fields — only relevant on a Session Booking rule whose payer='user'.
// Within that, slab-only fields only apply to selfPayPricingMode='slab', and the flat-only
// field only applies to 'flat'. GST and markup rate are partner-level (Contract Settings) —
// this rule only picks the mode + (for slab) the markup value on top of it.
// selfPayFlatSlabPricing (flatSlab mode's price-by-duration table) is NOT in this list —
// it's array-valued and rendered by its own bespoke row-editor block, not the generic
// single-value OUTPUTS renderer these constants gate.
const SELF_PAY_SLAB_ONLY_FIELDS = ['selfPaySlabSource', 'selfPayMarkupType', 'selfPayMarkupValue'];
const SELF_PAY_FLAT_ONLY_FIELDS = ['selfPayFlatAmount'];
const SELF_PAY_PRICING_FIELDS = ['selfPayPricingMode', ...SELF_PAY_SLAB_ONLY_FIELDS, ...SELF_PAY_FLAT_ONLY_FIELDS];

const NUM_OPS = [
  { value: 'equal', label: '=' },
  { value: 'notEqual', label: '≠' },
  { value: 'lessThan', label: '<' },
  { value: 'greaterThan', label: '>' },
  { value: 'lessThanInclusive', label: '≤' },
  { value: 'greaterThanInclusive', label: '≥' },
];

// ── Conversion helpers ─────────────────────────────────────────────────────────

function parseConditions(raw: unknown): {
  conditionMode: 'always' | 'when';
  condLogic: 'all' | 'any';
  condRows: ConditionRow[];
} {
  const obj = raw as Record<string, unknown> | null | undefined;
  if (!obj) return { conditionMode: 'always', condLogic: 'all', condRows: [] };

  const allRows = (obj['all'] as unknown[]) ?? [];
  const anyRows = (obj['any'] as unknown[]) ?? [];
  const condLogic: 'all' | 'any' = anyRows.length > 0 && allRows.length === 0 ? 'any' : 'all';
  const rows = condLogic === 'any' ? anyRows : allRows;

  if (!rows.length) return { conditionMode: 'always', condLogic, condRows: [] };

  return {
    conditionMode: 'when',
    condLogic,
    condRows: rows.map((r) => {
      const row = r as Record<string, unknown>;
      return {
        fact: String(row['fact'] ?? ''),
        operator: String(row['operator'] ?? 'equal'),
        value: (row['value'] ?? '') as string | number | boolean,
        ...(row['params'] ? { params: row['params'] as Record<string, unknown> } : {}),
      };
    }),
  };
}

function buildConditions(
  conditionMode: 'always' | 'when',
  condLogic: 'all' | 'any',
  condRows: ConditionRow[],
): unknown {
  if (conditionMode === 'always' || !condRows.length) return { all: [] };
  const validRows = condRows.filter((r) => r.fact && r.fact.trim() !== '');
  if (!validRows.length) return { all: [] };
  return {
    [condLogic]: validRows.map((r) => ({
      fact: r.fact,
      operator: r.operator,
      value: r.value,
      ...(r.params && Object.keys(r.params).length > 0 ? { params: r.params } : {}),
    })),
  };
}

function coerceScopeToArrays(scope: PolicyRule['scope'] | undefined): PolicyRuleUI['scope'] {
  const s = scope || {};
  return {
    ...s,
    verticalId: Array.isArray(s.verticalId) ? s.verticalId : s.verticalId ? [(s.verticalId as unknown as string)] : undefined,
    concernId: Array.isArray(s.concernId) ? s.concernId : s.concernId ? [(s.concernId as unknown as string)] : undefined,
    therapistIds: Array.isArray(s.therapistIds) ? s.therapistIds : s.therapistIds ? [(s.therapistIds as unknown as string)] : undefined,
  };
}

function toPolicyRuleUI(rule: PolicyRule, companyId: string): PolicyRuleUI {
  const { conditionMode, condLogic, condRows } = parseConditions(rule.conditions);
  return {
    id: rule.id,
    name: rule.name,
    description: rule.description,
    priority: rule.priority,
    active: rule.active,
    action: rule.action || 'Session Booking',
    companyId: rule.companyId || companyId,
    isGlobal: rule.isGlobal,
    userId: rule.userId,
    scope: coerceScopeToArrays(rule.scope),
    conditionMode,
    condLogic,
    condRows,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    outputs: (rule.outputs as Record<string, any>) || {},
    expanded: false,
    saving: false,
  };
}

function fromPolicyRuleUI(ui: PolicyRuleUI): PolicyRulePayload {
  const outputs = Object.fromEntries(
    Object.entries(ui.outputs).filter(([, v]) => v !== null && v !== undefined && v !== ''),
  );
  return {
    name: ui.name,
    priority: ui.priority,
    active: ui.active,
    action: ui.action,
    companyId: ui.companyId,
    scope: ui.scope,
    conditions: buildConditions(ui.conditionMode, ui.condLogic, ui.condRows),
    outputs,
  };
}

function toTfRuleUI(rule: PolicyRule): TfRuleUI {
  const outputs = (rule.outputs as Record<string, unknown>) || {};
  return {
    id: rule.id,
    name: rule.name,
    priority: rule.priority,
    active: rule.active,
    expanded: false,
    maxRate: (outputs['maxRate'] as number | null) ?? null,
    internalOnly: (outputs['internalOnly'] as boolean) ?? false,
    excludeTags: (outputs['excludeTags'] as string[]) ?? [],
    tagInput: '',
    saving: false,
  };
}

function fromTfRuleUI(ui: TfRuleUI, companyId: string): TherapistFilterRulePayload {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const outputs: Record<string, any> = {
    internalOnly: ui.internalOnly,
    excludeTags: ui.excludeTags,
  };
  if (ui.maxRate !== null && ui.maxRate !== undefined) {
    outputs['maxRate'] = ui.maxRate;
  }
  return {
    name: ui.name,
    priority: ui.priority,
    active: ui.active,
    companyId,
    conditions: { all: [] },
    outputs,
  };
}

// ── Component ──────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-policy-controls',
  templateUrl: './policy-controls.component.html',
})
export class PolicyControlsComponent extends AnyoComponent {
  companyId = '';
  loading = false;

  // Contract settings
  contract: ContractSettings = {};
  contractSaving = false;

  // Visibility (Vertical / Concern)
  visibilityLoading = false;
  visibilitySaving = false;
  visibilityDirty = false;
  visibilityVerticals: CompanyVisibilityVerticalRow[] = [];
  selectedVisibilityVertical: CompanyVisibilityVerticalRow | null = null;
  showConcernsDrawer = false;

  // Contract Benefits
  benefitVisibilityLoading = false;
  benefitVisibility: Partial<Record<ContractBenefit, boolean>> = {};
  benefitSaving: Partial<Record<ContractBenefit, boolean>> = {};

  // Policy rules (inline UI model)
  policyRulesUI: PolicyRuleUI[] = [];
  policyRulesLoading = false;

  // Therapist filter rules (inline UI model)
  tfRulesUI: TfRuleUI[] = [];
  tfRulesLoading = false;

  readonly ACTIONS = ['Session Booking', 'Event Registration', 'Diagnostic Order', 'Benefit Visibility'];
  readonly ALL_CONTRACT_BENEFITS = ALL_CONTRACT_BENEFITS;
  readonly BENEFIT_META: Record<ContractBenefit, { label: string; icon: string }> = {
    THERAPY_SESSIONS:  { label: 'Therapy Sessions',   icon: '🧠' },
    EVENTS_AND_CLASSES: { label: 'Events & Classes',  icon: '🎯' },
    DIAGNOSTICS:       { label: 'Diagnostics',          icon: '🩺' },
    LYFIS:             { label: 'Lyfis',               icon: '🔬' },
    RHYTHM:            { label: 'Rhythm',             icon: '💤' },
    FITNESS:           { label: 'Fitness',            icon: '🏃' },
    HEALTH_PROGRAMS:   { label: 'Health Programs',    icon: '💊' },
    KINESTEX:          { label: 'KinesteX',           icon: '🏋️' },
    CIRCLES:           { label: 'Circles',            icon: '💬' },
    CHALLENGES:        { label: 'Challenges',         icon: '🏆' },
    RESOURCE_LIBRARY:  { label: 'Resource Library',   icon: '📚' },
  };
  readonly FACTS = FACTS;
  readonly OUTPUTS = OUTPUTS;
  readonly NUM_OPS = NUM_OPS;

  // Scope dropdowns
  verticals: TherapyVerticals[] = [];
  concerns: TherapyConcerns[] = [];
  expertLevels: TherapistExpertLevel[] = [];
  therapists: IAnyoTherapist[] = [];

  constructor(
    protected override auth: AuthService,
    private route: ActivatedRoute,
    private router: Router,
    private policyService: PolicyService,
    private masterdataService: MasterdataService,
    private therapistService: TherapistService,
    private message: NzMessageService,
  ) {
    super(auth);
  }

  async ready(): Promise<void> {
    this.companyId = this.route.snapshot.params['id'];
    this.loadAll();
  }

  loadAll() {
    this.loading = true;
    let pending = 7;
    const done = () => { if (--pending <= 0) this.loading = false; };
    this.masterdataService.masterDataList().subscribe({
      next: (data) => {
        this.verticals = data.therapyVerticals ?? [];
        this.concerns = data.therapyConcerns ?? [];
        this.expertLevels = data.therapistExpertLevel ?? [];
        done();
      },
      error: () => { done(); },
    });
    this.therapistService.therapistList().subscribe({
      next: (data) => { this.therapists = data; done(); },
      error: () => { done(); },
    });
    this.policyService.getContractSettings(this.companyId).subscribe({
      next: (d) => { this.contract = d; done(); },
      error: () => { this.message.error('Failed to load contract settings'); done(); },
    });
    this.loadPolicyRules(done);
    this.loadTfRules(done);
    this.loadVisibility(done);
    this.loadBenefitVisibility(done);
  }

  loadBenefitVisibility(onDone?: () => void) {
    this.benefitVisibilityLoading = true;
    this.policyService.getBenefitVisibility(this.companyId).subscribe({
      next: (data: BenefitVisibilityResponse) => {
        this.benefitVisibility = data.benefits ?? {};
        this.benefitVisibilityLoading = false;
        onDone?.();
      },
      error: () => {
        this.benefitVisibilityLoading = false;
        onDone?.();
      },
    });
  }

  isBenefitEnabled(benefit: ContractBenefit): boolean {
    return this.benefitVisibility[benefit] !== false;
  }

  get activeBenefitCount(): number {
    return ALL_CONTRACT_BENEFITS.filter((b) => this.isBenefitEnabled(b)).length;
  }

  toggleBenefit(benefit: ContractBenefit, visible: boolean) {
    this.benefitVisibility[benefit] = visible;
    this.benefitSaving[benefit] = true;
    this.policyService.updateBenefitVisibility(this.companyId, { benefit, visible }).subscribe({
      next: () => {
        this.benefitSaving[benefit] = false;
        this.message.success(`${this.BENEFIT_META[benefit].label} ${visible ? 'enabled' : 'disabled'}`);
      },
      error: () => {
        this.benefitVisibility[benefit] = !visible; // revert
        this.benefitSaving[benefit] = false;
        this.message.error('Failed to update benefit');
      },
    });
  }

  // ── Policy rules ──

  loadPolicyRules(onDone?: () => void) {
    this.policyRulesLoading = true;
    this.policyService.getPolicyRules(this.companyId).subscribe({
      next: (rules) => {
        this.policyRulesUI = rules.map((r) => toPolicyRuleUI(r, this.companyId));
        this.policyRulesLoading = false;
        onDone?.();
      },
      error: () => {
        this.message.error('Failed to load booking rules');
        this.policyRulesLoading = false;
        onDone?.();
      },
    });
  }

  addRule() {
    this.policyRulesUI.unshift({
      name: '',
      priority: 10,
      active: true,
      action: 'Session Booking',
      companyId: this.companyId,
      isGlobal: false,
      scope: {},
      conditionMode: 'always',
      condLogic: 'all',
      condRows: [],
      outputs: {},
      expanded: true,
      saving: false,
    });
  }

  toggleRuleExpand(rule: PolicyRuleUI) { rule.expanded = !rule.expanded; }

  onRuleActionChange(rule: PolicyRuleUI) {
    rule.condRows = [];
    rule.outputs = {};
  }

  addCond(rule: PolicyRuleUI) {
    rule.condRows.push({ fact: '', operator: 'equal', value: '' });
  }

  removeCond(rule: PolicyRuleUI, idx: number) { rule.condRows.splice(idx, 1); }

  onFactChange(rule: PolicyRuleUI, row: ConditionRow) {
    const meta = (FACTS[rule.action] ?? []).find((f) => f.value === row.fact);
    row.operator = 'equal';
    row.value = meta?.type === 'bool' ? false : '';
    row.params = meta?.hasParams ? { days: 7 } : undefined;
  }

  factType(action: string, fact: string): 'number' | 'bool' {
    return (FACTS[action] ?? []).find((f) => f.value === fact)?.type ?? 'number';
  }

  onScopeChange(rule: PolicyRuleUI) {
    this._factsCache.clear();
    // When verticals change, remove concern selections that no longer belong to any selected vertical
    if (rule.scope?.verticalId?.length && rule.scope?.concernId?.length) {
      const validConcernIds = new Set(this.concernsForRule(rule).map((c) => c.m_id));
      rule.scope.concernId = rule.scope.concernId.filter((id) => validConcernIds.has(id));
    } else if (!rule.scope?.verticalId?.length) {
      // No vertical selected = Any — clear concern filter too
      rule.scope.concernId = [];
    }

    const valid = new Set(this.factsFor(rule.action, rule).map((f) => f.value));
    let clearedCount = 0;
    rule.condRows.forEach((row) => {
      if (row.fact && !valid.has(row.fact)) {
        row.fact = '';
        row.operator = 'equal';
        row.value = '';
        clearedCount++;
      }
    });
    if (clearedCount > 0) {
      this.message.warning(`${clearedCount} condition${clearedCount > 1 ? 's' : ''} cleared — the selected fact is no longer valid for this scope.`);
    }
  }

  concernsForRule(rule: PolicyRuleUI) {
    if (!rule.scope?.verticalId?.length) return this.concerns;
    const verticalSet = new Set(rule.scope.verticalId);
    return this.concerns.filter((c) => c.verticalId?.some((vid) => verticalSet.has(vid)));
  }

  private resolveNames(ids: string[] | undefined, list: { m_id: string; data: string }[]): string {
    if (!ids?.length) return '';
    const names = ids.map((id) => list.find((x) => x.m_id === id)?.data).filter((n): n is string => !!n);
    return names.join(', ');
  }

  private factDescription(value: string, vNames: string, cNames: string): string {
    switch (value) {
      case 'sessionsInLastNDays':
        return 'Rolling window count for the vertical + concern being booked right now';
      case 'sessionsConsumedInPeriod':
        return 'Sessions in whichever concern is being booked now';
      case 'sessionsConsumedInPeriodForVertical':
        return 'Sessions in whichever vertical is being booked now';
      case 'totalSessionsConsumedInPeriod':
        return 'Total sessions across all verticals';
      case 'sessionsConsumedInPeriodForScopeVerticals':
        return `Sum of sessions across ${vNames || 'selected verticals'} combined`;
      case 'maxSessionsConsumedInPeriodAcrossScopeVerticals':
        return `Highest session count from any single vertical among: ${vNames || 'selected verticals'}`;
      case 'sessionsConsumedInPeriodForScopeConcerns':
        return `Sum of sessions across ${cNames || 'selected concerns'} combined`;
      case 'maxSessionsConsumedInPeriodAcrossScopeConcerns':
        return `Highest session count from any single concern among: ${cNames || 'selected concerns'}`;
      case 'sessionsConsumedInPeriodForScopeVerticalAndConcern':
        return `Sessions matching both verticals (${vNames || '…'}) and concerns (${cNames || '…'})`;
      case 'lastSessionDateForScopeVerticals':
        return `Date of most recent session in any of: ${vNames || 'selected verticals'}`;
      default:
        return '';
    }
  }

  private _factsCache = new Map<string, { value: string; label: string; type: 'number' | 'bool'; description?: string; hasParams?: boolean; paramsLabel?: string }[]>();

  factsFor(action: string, rule?: PolicyRuleUI) {
    const all = FACTS[action] ?? [];
    if (!rule) return all;

    const cacheKey = `${action}|${(rule.scope?.verticalId ?? []).join(',')}|${(rule.scope?.concernId ?? []).join(',')}`;
    if (this._factsCache.has(cacheKey)) return this._factsCache.get(cacheKey)!;

    const hasMultiVerticals = (rule.scope?.verticalId?.length ?? 0) > 1;
    const hasMultiConcerns = (rule.scope?.concernId?.length ?? 0) > 1;
    const hasConcerns = (rule.scope?.concernId?.length ?? 0) > 0;
    const hasScopeArrays = hasMultiVerticals || hasMultiConcerns;

    const vNames = this.resolveNames(rule.scope?.verticalId, this.verticals);
    const cNames = this.resolveNames(rule.scope?.concernId, this.concerns);
    const withDesc = (f: typeof all[0]) => ({ ...f, description: this.factDescription(f.value, vNames, cNames) });

    const singleScopeFacts = ['sessionsConsumedInPeriod', 'sessionsConsumedInPeriodForVertical', 'totalSessionsConsumedInPeriod'];
    const multiScopeFacts = [
      'sessionsConsumedInPeriodForScopeVerticals',
      'maxSessionsConsumedInPeriodAcrossScopeVerticals',
      'sessionsConsumedInPeriodForScopeConcerns',
      'maxSessionsConsumedInPeriodAcrossScopeConcerns',
      'sessionsConsumedInPeriodForScopeVerticalAndConcern',
      'lastSessionDateForScopeVerticals',
    ];

    if (hasScopeArrays) {
      const result = all.filter((f) => {
        if (f.value === 'sessionsConsumedInPeriodForVertical') return hasMultiVerticals;
        if (f.value === 'sessionsConsumedInPeriod') return hasConcerns;
        if (f.value === 'totalSessionsConsumedInPeriod') return false;
        if (f.value === 'sessionsConsumedInPeriodForScopeVerticals') return hasMultiVerticals;
        if (f.value === 'maxSessionsConsumedInPeriodAcrossScopeVerticals') return hasMultiVerticals;
        if (f.value === 'sessionsConsumedInPeriodForScopeConcerns') return hasMultiConcerns;
        if (f.value === 'maxSessionsConsumedInPeriodAcrossScopeConcerns') return hasMultiConcerns;
        if (f.value === 'sessionsConsumedInPeriodForScopeVerticalAndConcern') return hasMultiVerticals && hasConcerns;
        if (f.value === 'lastSessionDateForScopeVerticals') return hasMultiVerticals;
        return true;
      }).map(withDesc);
      this._factsCache.set(cacheKey, result);
      return result;
    }

    let correctSessionFact: string;
    if (rule.scope?.concernId?.length) {
      correctSessionFact = 'sessionsConsumedInPeriod';
    } else if (rule.scope?.verticalId?.length) {
      correctSessionFact = 'sessionsConsumedInPeriodForVertical';
    } else {
      correctSessionFact = 'totalSessionsConsumedInPeriod';
    }

    const result = all
      .filter(
        (f) =>
          !multiScopeFacts.includes(f.value) &&
          (!singleScopeFacts.includes(f.value) || f.value === correctSessionFact),
      )
      .map(withDesc);
    this._factsCache.set(cacheKey, result);
    return result;
  }
  outputsFor(action: string) { return OUTPUTS[action] ?? []; }

  /** True when this output should be shown at all — self-pay pricing fields are hidden
   * (not just disabled) when they don't apply, to avoid cluttering every non-self-pay rule
   * with 5 irrelevant fields. */
  isFieldVisible(rule: PolicyRuleUI, out: { key: string }): boolean {
    if (!SELF_PAY_PRICING_FIELDS.includes(out.key)) return true;
    if (rule.outputs['payer'] !== 'user') return false;
    if (out.key === 'selfPayPricingMode') return true;
    const mode = rule.outputs['selfPayPricingMode'];
    if (SELF_PAY_SLAB_ONLY_FIELDS.includes(out.key)) return mode === 'slab';
    if (SELF_PAY_FLAT_ONLY_FIELDS.includes(out.key)) return mode === 'flat';
    return true;
  }

  /** Clears self-pay pricing fields that just became irrelevant — payer changed away from
   * 'user', or selfPayPricingMode changed away from the mode a field belongs to. */
  onSelfPayOutputChange(rule: PolicyRuleUI, changedKey: string): void {
    if (changedKey === 'payer' && rule.outputs['payer'] !== 'user') {
      for (const key of SELF_PAY_PRICING_FIELDS) rule.outputs[key] = '';
      rule.outputs['selfPayFlatSlabPricing'] = [];
      return;
    }
    if (changedKey === 'selfPayPricingMode') {
      const mode = rule.outputs['selfPayPricingMode'];
      if (mode !== 'slab') for (const key of SELF_PAY_SLAB_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flat') for (const key of SELF_PAY_FLAT_ONLY_FIELDS) rule.outputs[key] = '';
      if (mode !== 'flatSlab') rule.outputs['selfPayFlatSlabPricing'] = [];
    }
  }

  /** True when the flatSlab price-by-duration row editor should be shown for this rule. */
  showFlatSlabEditor(rule: PolicyRuleUI): boolean {
    return rule.outputs['payer'] === 'user' && rule.outputs['selfPayPricingMode'] === 'flatSlab';
  }

  flatSlabRows(rule: PolicyRuleUI): { minDuration: number | null; maxDuration: number | null; amount: number | null }[] {
    return (rule.outputs['selfPayFlatSlabPricing'] as { minDuration: number | null; maxDuration: number | null; amount: number | null }[]) ?? [];
  }

  addFlatSlabRow(rule: PolicyRuleUI): void {
    if (!Array.isArray(rule.outputs['selfPayFlatSlabPricing'])) {
      rule.outputs['selfPayFlatSlabPricing'] = [];
    }
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[]).push({
      minDuration: null,
      maxDuration: null,
      amount: null,
    });
  }

  removeFlatSlabRow(rule: PolicyRuleUI, idx: number): void {
    (rule.outputs['selfPayFlatSlabPricing'] as unknown[])?.splice(idx, 1);
  }

  isActionBlocked(rule: PolicyRuleUI): boolean {
    const field = ACTION_ALLOWED_FIELD[rule.action];
    if (!field) return false;
    return rule.outputs[field] === 'false' || rule.outputs[field] === false;
  }

  isOutputDisabled(rule: PolicyRuleUI, out: { key: string }): boolean {
    if (!this.isActionBlocked(rule)) return false;
    return (BLOCKED_CLEARS[rule.action] ?? []).includes(out.key);
  }

  onAllowedFieldChange(rule: PolicyRuleUI): void {
    if (!this.isActionBlocked(rule)) return;
    for (const key of BLOCKED_CLEARS[rule.action] ?? []) {
      rule.outputs[key] = '';
    }
  }

  saveRule(rule: PolicyRuleUI) {
    if (!rule.name.trim()) { this.message.warning('Rule name is required'); return; }
    if (this.isActionBlocked(rule)) {
      const blocked = BLOCKED_CLEARS[rule.action] ?? [];
      const hasConflict = blocked.some(
        (k) => rule.outputs[k] !== '' && rule.outputs[k] != null,
      );
      if (hasConflict) {
        this.message.error('Cannot set payer or session options when booking is blocked — clear those fields first.');
        return;
      }
    }
    rule.saving = true;
    const payload = fromPolicyRuleUI(rule);
    const call = rule.id
      ? this.policyService.updatePolicyRule(rule.id, payload)
      : this.policyService.createPolicyRule(payload);
    call.subscribe({
      next: () => {
        this.message.success('Rule saved');
        rule.saving = false;
        rule.expanded = false;
        this.loadPolicyRules();
      },
      error: () => { this.message.error('Failed to save rule'); rule.saving = false; },
    });
  }

  deleteRule(rule: PolicyRuleUI) {
    if (!rule.id) { this.policyRulesUI = this.policyRulesUI.filter((r) => r !== rule); return; }
    this.policyService.deletePolicyRule(rule.id).subscribe({
      next: () => {
        this.message.success('Rule deleted');
        this.policyRulesUI = this.policyRulesUI.filter((r) => r !== rule);
      },
      error: () => this.message.error('Failed to delete rule'),
    });
  }

  generateDescription(rule: PolicyRuleUI) {
    if (!rule.id) {
      this.message.warning('Save the rule first before generating a description');
      return;
    }
    rule.saving = true;
    this.policyService.describeRule(rule.id).subscribe({
      next: (res) => {
        rule.description = res.description;
        rule.saving = false;
        this.message.success('Description generated');
      },
      error: (err) => {
        rule.saving = false;
        const msg = err?.error?.message || 'Failed to generate description';
        this.message.error(msg);
      },
    });
  }

  // ── Therapist filter rules ──

  loadTfRules(onDone?: () => void) {
    this.tfRulesLoading = true;
    this.policyService.getTherapistFilterRules(this.companyId).subscribe({
      next: (rules) => {
        this.tfRulesUI = rules.map(toTfRuleUI);
        this.tfRulesLoading = false;
        onDone?.();
      },
      error: () => { this.tfRulesLoading = false; onDone?.(); },
    });
  }

  addTfRule() {
    this.tfRulesUI.unshift({
      name: '',
      priority: 10,
      active: true,
      expanded: true,
      maxRate: null,
      internalOnly: false,
      excludeTags: [],
      tagInput: '',
      saving: false,
    });
  }

  toggleTfExpand(rule: TfRuleUI) { rule.expanded = !rule.expanded; }

  addTag(rule: TfRuleUI) {
    const tag = (rule.tagInput || '').trim().toUpperCase();
    if (tag && !rule.excludeTags.includes(tag)) rule.excludeTags.push(tag);
    rule.tagInput = '';
  }

  removeTag(rule: TfRuleUI, tag: string) {
    rule.excludeTags = rule.excludeTags.filter((t) => t !== tag);
  }

  saveTfRule(rule: TfRuleUI) {
    if (!rule.name.trim()) { this.message.warning('Rule name is required'); return; }
    rule.saving = true;
    const payload = fromTfRuleUI(rule, this.companyId);
    const call = rule.id
      ? this.policyService.updateTherapistFilterRule(rule.id, payload)
      : this.policyService.createTherapistFilterRule(payload);
    call.subscribe({
      next: () => {
        this.message.success('Rule saved');
        rule.saving = false;
        rule.expanded = false;
        this.loadTfRules();
      },
      error: () => { this.message.error('Failed to save rule'); rule.saving = false; },
    });
  }

  deleteTfRule(rule: TfRuleUI) {
    if (!rule.id) { this.tfRulesUI = this.tfRulesUI.filter((r) => r !== rule); return; }
    this.policyService.deleteTherapistFilterRule(rule.id).subscribe({
      next: () => {
        this.message.success('Rule deleted');
        this.tfRulesUI = this.tfRulesUI.filter((r) => r !== rule);
      },
      error: () => this.message.error('Failed to delete rule'),
    });
  }

  // ── Visibility ──

  loadVisibility(onDone?: () => void) {
    this.visibilityLoading = true;
    this.policyService.getCompanyVisibility(this.companyId).subscribe({
      next: (data) => {
        this.visibilityVerticals = data.verticals ?? [];
        this.visibilityDirty = false;
        this.visibilityLoading = false;
        onDone?.();
      },
      error: () => {
        this.message.error('Failed to load visibility settings');
        this.visibilityLoading = false;
        onDone?.();
      },
    });
  }

  markVisibilityDirty() { this.visibilityDirty = true; }

  toggleVerticalVisible(v: CompanyVisibilityVerticalRow, visible: boolean) {
    v.visible = visible;
    this.markVisibilityDirty();
  }

  openConcerns(vertical: CompanyVisibilityVerticalRow) {
    this.selectedVisibilityVertical = vertical;
    this.showConcernsDrawer = true;
  }

  closeConcernsDrawer() {
    this.showConcernsDrawer = false;
    this.selectedVisibilityVertical = null;
  }

  hideAllConcerns() {
    if (!this.selectedVisibilityVertical) return;
    for (const c of this.selectedVisibilityVertical.concerns ?? []) c.hidden = true;
    this.markVisibilityDirty();
  }

  showAllConcerns() {
    if (!this.selectedVisibilityVertical) return;
    for (const c of this.selectedVisibilityVertical.concerns ?? []) c.hidden = false;
    this.markVisibilityDirty();
  }

  saveVisibility() {
    const hiddenVerticalIds = this.visibilityVerticals.filter((v) => !v.visible).map((v) => v.verticalId);
    const hiddenConcernIds = this.visibilityVerticals.flatMap((v) =>
      (v.concerns ?? []).filter((c) => c.hidden).map((c) => c.concernId),
    );
    const payload: CompanyVisibilityUpdateRequest = { hiddenVerticalIds, hiddenConcernIds };
    this.visibilitySaving = true;
    this.policyService.updateCompanyVisibility(this.companyId, payload).subscribe({
      next: () => { this.message.success('Visibility saved'); this.visibilitySaving = false; this.visibilityDirty = false; },
      error: () => { this.message.error('Failed to save visibility'); this.visibilitySaving = false; },
    });
  }

  discardVisibility() { this.loadVisibility(); }

  // ── Contract ──

  policyEngineValidationError = '';

  onPolicyEngineToggle(enabling: boolean) {
    if (!enabling) {
      this.contract.policyControlsEnabled = false;
      this.policyEngineValidationError = '';
      return;
    }
    // Validate prerequisites before allowing enable
    if (!this.contract.policyPoolMode) {
      this.policyEngineValidationError = 'Set the session pool model before enabling the policy engine.';
      return;
    }
    if (
      this.contract.policyPoolMode === 'company' &&
      (this.contract.policyPurchasedSessionPool === undefined ||
        this.contract.policyPurchasedSessionPool === null ||
        this.contract.policyPurchasedSessionPool <= 0)
    ) {
      this.policyEngineValidationError = 'Enter a pool size (> 0) for company pool mode before enabling the policy engine.';
      return;
    }
    this.policyEngineValidationError = '';
    this.contract.policyControlsEnabled = true;
  }

  saveContractSettings() {
    this.contractSaving = true;
    this.policyService.updateContractSettings(this.companyId, this.contract).subscribe({
      next: () => { this.message.success('Contract settings saved'); this.contractSaving = false; },
      error: () => { this.message.error('Failed to save contract settings'); this.contractSaving = false; },
    });
  }

  // ── Summary helpers for collapsed rule header ──

  scopeSummary(rule: PolicyRuleUI): string {
    if (rule.action === 'Benefit Visibility') {
      return rule.scope.benefitCategory
        ? this.BENEFIT_META[rule.scope.benefitCategory]?.label ?? rule.scope.benefitCategory
        : 'All benefits';
    }
    const parts = [
      rule.scope.verticalId?.length
        ? rule.scope.verticalId.map((id) => this.verticals.find((v) => v.m_id === id)?.data ?? id).join(', ')
        : null,
      rule.scope.concernId?.length
        ? rule.scope.concernId.map((id) => this.concerns.find((c) => c.m_id === id)?.data ?? id).join(', ')
        : null,
      rule.scope.expertLevelId
        ? (this.expertLevels.find((e) => e.m_id === rule.scope.expertLevelId)?.data ?? rule.scope.expertLevelId)
        : null,
      rule.scope.therapistIds?.length
        ? rule.scope.therapistIds.map((id) => this.therapistDisplayName(id)).join(', ')
        : null,
    ].filter(Boolean);
    return parts.length ? parts.join(' · ') : 'All';
  }

  condSummary(rule: PolicyRuleUI): string {
    if (rule.conditionMode === 'always' || !rule.condRows.length) return 'Always applies';
    const opMap: Record<string, string> = {
      equal: '=', notEqual: '≠', lessThan: '<', greaterThan: '>',
      lessThanInclusive: '≤', greaterThanInclusive: '≥',
    };
    const sep = rule.condLogic === 'any' ? ' OR ' : ' AND ';
    const parts = rule.condRows.slice(0, 2).map((c) => {
      if (!c.fact) return '?';
      if (this.factType(rule.action, c.fact) === 'bool') return `${c.fact}=${c.value ? 'yes' : 'no'}`;
      return `${c.fact} ${opMap[c.operator] ?? c.operator} ${c.value}`;
    });
    return 'When ' + parts.join(sep) + (rule.condRows.length > 2 ? ' …' : '');
  }

  thenSummary(rule: PolicyRuleUI): string {
    const outs = OUTPUTS[rule.action] ?? [];
    const set = outs.filter(
      (o) => rule.outputs[o.key] !== '' && rule.outputs[o.key] !== undefined && rule.outputs[o.key] !== null,
    );
    if (!set.length) return '(no outputs)';
    const short: Record<string, string> = {
      payer: 'payer', sessionMinutes: 'duration', sessionAllowed: 'allowed',
      eventAllowed: 'allowed', diagAllowed: 'allowed', cooldown: 'cooldown',
      blockReason: 'reason',
    };
    return (
      set.slice(0, 3).map((o) => {
        const label = short[o.key] ?? o.key;
        const val = o.key === 'blockReason' ? '✓' : rule.outputs[o.key];
        return `${label}=${val}`;
      }).join(' · ') +
      (set.length > 3 ? ` +${set.length - 3}` : '')
    );
  }

  // ── Getters ──

  get bookingRulesUI() { return this.policyRulesUI.filter((r) => r.action !== 'Benefit Visibility'); }
  get activePolicyRulesCount() { return this.bookingRulesUI.filter((r) => r.active).length; }
  get globalRulesCount() { return this.bookingRulesUI.filter((r) => r.isGlobal).length; }
  // Company rules exclude any rule carrying a userId — those are personal overrides, shown
  // separately (read-only) below, never mixed into the editable company-rule list.
  get companyRulesUI() { return this.policyRulesUI.filter((r) => !r.isGlobal && !r.userId && r.action !== 'Benefit Visibility'); }
  get globalRulesUI() { return this.policyRulesUI.filter((r) => r.isGlobal && r.action !== 'Benefit Visibility'); }
  // Personal (per-user) rule overrides under this company — read-only context view.
  // Sourced from the SAME getPolicyRules(companyId) response as company/global rules
  // (rules collection is shared and companyId-keyed); requires the backend to include
  // `userId` on rule docs that carry one. See notes/anyo-portal-update.md for the flagged gap.
  get personalRulesUI() { return this.policyRulesUI.filter((r) => !!r.userId && r.action !== 'Benefit Visibility'); }

  therapistDisplayName(therapistId: string | undefined): string {
    if (!therapistId) return '';
    const t = this.therapists.find((x) => x._id === therapistId);
    return t ? `${t.title} ${t.firstName} ${t.lastName}`.trim() : `${therapistId} (not found)`;
  }
  get benefitCompanyRulesUI() { return this.policyRulesUI.filter((r) => !r.isGlobal && r.action === 'Benefit Visibility'); }
  get benefitGlobalRulesUI() { return this.policyRulesUI.filter((r) => r.isGlobal && r.action === 'Benefit Visibility'); }

  addBenefitRule() {
    this.policyRulesUI.unshift({
      name: '',
      priority: 10,
      active: true,
      action: 'Benefit Visibility',
      companyId: this.companyId,
      isGlobal: false,
      scope: {},
      conditionMode: 'always',
      condLogic: 'all',
      condRows: [],
      outputs: {},
      expanded: true,
      saving: false,
    });
  }
  get activeTfRulesCount() { return this.tfRulesUI.filter((r) => r.active).length; }
  get hiddenVerticalCount() { return this.visibilityVerticals.filter((v) => !v.visible).length; }

  hiddenConcernsCountFor(vertical: CompanyVisibilityVerticalRow) {
    return (vertical.concerns ?? []).filter((c) => c.hidden).length;
  }

  verticalImage(verticalId: string): string {
    return this.verticals.find((v) => v.m_id === verticalId)?.image ?? '';
  }

  goBack() { this.router.navigate(['/partner', 'add', this.companyId]); }
}
